'use client';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ptBR } from 'date-fns/locale';

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
    onDateChange: (startDate: string | undefined, endDate: string | undefined) => void;
    initialDateRange?: DateRange;
}

export interface DateRangePickerRef {
    clear: () => void;
}

export const DateRangePicker = React.forwardRef<DateRangePickerRef, DateRangePickerProps>(({
    className,
    onDateChange,
    initialDateRange
}, ref) => {
    const [date, setDate] = React.useState<DateRange | undefined>(initialDateRange);
    const [open, setOpen] = React.useState(false);

    const handleDateSelect = React.useCallback((newDate: DateRange | undefined) => {
        setDate(newDate);
    }, []);

    const handleClear = React.useCallback(() => {
        setDate(undefined);
        onDateChange(undefined, undefined);
        setOpen(false);
    }, [onDateChange]);

    const handleApply = React.useCallback(() => {
        if (date?.from || date?.to) {
            const startDate = date.from ? format(date.from, 'yyyy-MM-dd') : undefined;
            const endDate = date.to ? format(date.to, 'yyyy-MM-dd') : undefined;
            onDateChange(startDate, endDate);
        }
        setOpen(false);
    }, [date, onDateChange]);

    React.useImperativeHandle(ref, () => ({
        clear: handleClear
    }), [handleClear]);

    const displayText = React.useMemo(() => {
        if (date?.from) {
            if (date.to) {
                return `${format(date.from, 'dd/MM/yyyy')} - ${format(date.to, 'dd/MM/yyyy')}`;
            }
            return format(date.from, 'dd/MM/yyyy');
        }
        return 'Selecione um intervalo de datas';
    }, [date]);

    return (
        <div className={cn('grid gap-2', className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={'outline'}
                        className={cn(
                            'h-8 w-auto justify-start text-left font-normal text-sm',
                            !date && 'text-muted-foreground'
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {displayText}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        initialFocus
                        mode="range"
                        locale={ptBR}
                        defaultMonth={date?.from}
                        selected={date}
                        onSelect={handleDateSelect}
                        numberOfMonths={2}
                    />
                    <div className="border-t bg-white p-3 sm:p-4 mt-auto shrink-0">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                            <p className="text-xs text-gray-500 order-2 sm:order-1">Clique nas datas para selecionar</p>
                            <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-3 text-xs flex-1 sm:flex-none"
                                    onClick={handleClear}
                                >
                                    Limpar
                                </Button>
                                <Button
                                    size="sm"
                                    className="h-7 px-3 text-xs flex-1 sm:flex-none"
                                    onClick={handleApply}
                                >
                                    Aplicar
                                </Button>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
});

DateRangePicker.displayName = "DateRangePicker";
